import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Button, Card, Col, Row, Table } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import DOMPurify from 'dompurify';

// การ์ดบันทึกการสอน 1 รายการ สำหรับมุมมองมือถือ
const TrackingRecordCard = ({ r, idx, formatTime }) => {
  const total = Number(r.total_students) || 0;
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="fw-semibold">
          #{idx + 1} {r.att_date_th ? `${r.att_day_th ? r.att_day_th + ' ' : ''}${r.att_date_th}` : '-'}
        </div>
        <span className="fs-xs text-muted">{formatTime(r.created_at)} · คาบ {r.period_order || '-'}</span>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div>{r.tr_topic || <span className="text-muted fst-italic">ไม่ระบุหัวข้อ</span>}</div>
      </div>
      <div className="d-flex flex-wrap gap-3 mt-2 pt-2 border-top fs-sm">
        <span className="text-success">มา {total > 0 ? Number(r.presents) || 0 : '-'}</span>
        <span className="text-danger">ขาด {total > 0 ? Number(r.absents) || 0 : '-'}</span>
        <span className="text-warning">สาย {total > 0 ? Number(r.lates) || 0 : '-'}</span>
        <span className="text-info">ลา {total > 0 ? Number(r.leaves) || 0 : '-'}</span>
        <span className="text-muted">รวม {total > 0 ? total : '-'}</span>
      </div>
    </div>
  )
}

export default function ListTeachingTracking() {
  const { teach_id } = useParams();
  const { setLoading } = useLoading();

  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  };

  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [context, setContext] = useState(null);
  const [records, setRecords] = useState([]);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  useEffect(() => {
    if (!teach_id) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getTeachTrackingByTeachId/${teach_id}`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
      .then(res => {
        setContext(res.data.context);
        setRecords(res.data.records);
      })
      .catch(err => {
        console.error('Error loading teach tracking:', err);
      })
      .finally(() => setLoading(false));
  }, [teach_id, setLoading]);

  const formatTime = (created_at) =>
    created_at ? new Date(created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '-';

  const getContextLabel = () => {
    if (!context) return '';
    return `${context.sub_subid} ${context.sub_subname}${context.sub_type ? ' ' + context.sub_type : ''} | ชั้น ม.${context.tb_class_c_id}/${context.r_no}${context.p_code ? ` (${context.p_code})` : ''} | ภาคเรียนที่ ${context.sub_semester} | ${context.t_name} | ปีการศึกษา ${context.year_year}`;
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const infoRows = [
      ['รายการบันทึกการสอน'],
      [getContextLabel()],
      [`ส่งออก: ${new Date().toLocaleString('th-TH')}`],
      [],
      ['#', 'วันที่', 'เวลา', 'คาบ', 'หัวข้อที่สอน', 'มาเรียน', 'ขาด', 'สาย', 'ลา', 'รวม']
    ];
    records.forEach((r, idx) => {
      const total = Number(r.total_students) || 0;
      infoRows.push([
        idx + 1,
        r.att_date_th ? `${r.att_day_th ? r.att_day_th + ' ' : ''}${r.att_date_th}` : '-',
        formatTime(r.created_at),
        r.period_order || '-',
        r.tr_topic || 'ไม่ระบุหัวข้อ',
        total > 0 ? Number(r.presents) || 0 : '-',
        total > 0 ? Number(r.absents) || 0 : '-',
        total > 0 ? Number(r.lates) || 0 : '-',
        total > 0 ? Number(r.leaves) || 0 : '-',
        total > 0 ? total : '-'
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(infoRows);
    ws['!cols'] = [{ wch: 5 }, { wch: 14 }, { wch: 10 }, { wch: 6 }, { wch: 40 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }];
    XLSX.utils.book_append_sheet(wb, ws, 'บันทึกการสอน');
    const filename = context ? `บันทึกการสอน_${context.sub_subid}.xlsx` : `บันทึกการสอน_${teach_id}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handlePrint = () => {
    const rowsHtml = records.map((r, idx) => {
      const total = Number(r.total_students) || 0;
      const dateCell = r.att_date_th ? `${r.att_day_th ? r.att_day_th + ' ' : ''}${r.att_date_th}` : '-';
      return `<tr>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${idx + 1}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${dateCell}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${formatTime(r.created_at)}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${r.period_order || '-'}</td>
        <td style="border:1px solid #999;padding:3px;">${r.tr_topic || 'ไม่ระบุหัวข้อ'}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${total > 0 ? Number(r.presents) || 0 : '-'}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${total > 0 ? Number(r.absents) || 0 : '-'}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${total > 0 ? Number(r.lates) || 0 : '-'}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${total > 0 ? Number(r.leaves) || 0 : '-'}</td>
        <td style="border:1px solid #999;padding:3px;text-align:center;">${total > 0 ? total : '-'}</td>
      </tr>`;
    }).join('');

    const unsafeHtml = `
      <h3 style="margin:0 0 6px 0;">รายการบันทึกการสอน</h3>
      <div style="margin-bottom:4px;">${getContextLabel()}</div>
      <div style="margin-bottom:16px;color:#888;">พิมพ์: ${new Date().toLocaleString('th-TH')}</div>
      <table style="border-collapse:collapse;width:100%;font-size:13px;">
        <thead>
          <tr style="background:#ddeeff;">
            <th style="border:1px solid #999;padding:4px;text-align:center;width:40px;">#</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:130px;">วันที่</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:70px;">เวลา</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:50px;">คาบ</th>
            <th style="border:1px solid #999;padding:4px;">หัวข้อที่สอน</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:50px;">มา</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:50px;">ขาด</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:50px;">สาย</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:50px;">ลา</th>
            <th style="border:1px solid #999;padding:4px;text-align:center;width:50px;">รวม</th>
          </tr>
        </thead>
        <tbody>${rowsHtml || '<tr><td colspan="10" style="border:1px solid #999;padding:8px;text-align:center;">ไม่มีข้อมูล</td></tr>'}</tbody>
      </table>
    `;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>บันทึกการสอน</title>
      <style>
        body { font-family: 'Cordia New', Tahoma, Arial, sans-serif; font-size: 14px; padding: 16px; color: #111; }
        table { border-collapse: collapse; width: 100%; }
        @media print { @page { size: A4 landscape; margin: 10mm; } }
      </style></head>
      <body onload="window.print()">${DOMPurify.sanitize(unsafeHtml)}</body></html>`;
    const win = window.open('', '_blank', 'width=1100,height=800');
    win.document.write(html);
    win.document.close();
  };

  const handleExportPdf = async () => {
    const { default: html2canvas } = await import('html2canvas');
    const rowsHtml = records.map((r, idx) => {
      const total = Number(r.total_students) || 0;
      return `<tr>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${idx + 1}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${r.att_date_th ? `${r.att_day_th ? r.att_day_th + ' ' : ''}${r.att_date_th}` : '-'}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${formatTime(r.created_at)}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${r.period_order || '-'}</td>
        <td style="border:1px solid #ddd;padding:5px;">${r.tr_topic || 'ไม่ระบุหัวข้อ'}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${total > 0 ? Number(r.presents) || 0 : '-'}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${total > 0 ? Number(r.absents) || 0 : '-'}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${total > 0 ? Number(r.lates) || 0 : '-'}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${total > 0 ? Number(r.leaves) || 0 : '-'}</td>
        <td style="border:1px solid #ddd;padding:5px;text-align:center;">${total > 0 ? total : '-'}</td>
      </tr>`;
    }).join('');

    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;left:-99999px;top:0;width:750px;padding:24px;background:#fff;color:#111;font-family:Tahoma,Arial,sans-serif;font-size:12px;';
    const unsafeHtml = `
      <h3 style="margin:0 0 6px 0;">รายการบันทึกการสอน</h3>
      <div style="margin-bottom:4px;">${getContextLabel()}</div>
      <div style="margin-bottom:16px;color:#888;">ส่งออก: ${new Date().toLocaleString('th-TH')}</div>
      <table style="border-collapse:collapse;width:100%;">
        <thead>
          <tr style="background:#f1f3f5;">
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:40px;">#</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:110px;">วันที่</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:75px;">เวลา</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:55px;">คาบ</th>
            <th style="border:1px solid #ddd;padding:5px;">หัวข้อที่สอน</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:55px;">มา</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:55px;">ขาด</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:55px;">สาย</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:55px;">ลา</th>
            <th style="border:1px solid #ddd;padding:5px;text-align:center;width:55px;">รวม</th>
          </tr>
        </thead>
        <tbody>${rowsHtml || '<tr><td colspan="10" style="border:1px solid #ddd;padding:8px;text-align:center;">ไม่มีข้อมูล</td></tr>'}</tbody>
      </table>
    `;
    el.innerHTML = DOMPurify.sanitize(unsafeHtml);
    document.body.appendChild(el);
    const canvas = await html2canvas(el, { scale: 2 });
    document.body.removeChild(el);

    const imgW = 595.28; // A4 portrait pt
    const imgH = (canvas.height * imgW) / canvas.width;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    doc.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgW, imgH);
    const filename = context ? `บันทึกการสอน_${context.sub_subid}.pdf` : `บันทึกการสอน_${teach_id}.pdf`;
    doc.save(filename);
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลการเรียนการสอน</Link></li>
              <li className="breadcrumb-item">
                <Link to="/teaching/teaching-tracking">ติดตามการสอน</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">บันทึกการสอน</li>
            </ol>
            <h4 className="main-title mb-0">บันทึกการสอน</h4>
          </div>
        </div>

        {context && (
          <Row className="g-3 mb-3">
            <Col xl="12">
              <Card className="card-one">
                <Card.Body className="p-3">
                  <Row className="g-2 align-items-center">
                    <Col xs="auto">
                      <span className="fw-semibold fs-5 text-primary">{context.sub_subid}</span>
                    </Col>
                    <Col>
                      <span className="fw-semibold fs-6">{context.sub_subname}</span>
                      {context.sub_type && <span className="text-muted ms-1">{context.sub_type}</span>}
                    </Col>
                    <Col xs="auto" className="d-flex gap-3 text-muted fs-sm">
                      <span><i className="ri-government-line me-1"></i>ชั้น ม.{context.tb_class_c_id}/{context.r_no}{context.p_code ? ` (${context.p_code})` : ""}</span>
                      <span><i className="ri-calendar-line me-1"></i>ภาคเรียนที่ {context.sub_semester}</span>
                      <span><i className="ri-user-line me-1"></i>{context.t_name}</span>
                      <span><i className="ri-book-open-line me-1"></i>ปีการศึกษา {context.year_year}</span>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="g-3 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title as="h6" className="mb-0">
                  รายการบันทึกการสอน
                  {records.length > 0 && (
                    <Badge bg="secondary" className="ms-2">{records.length} ครั้ง</Badge>
                  )}
                </Card.Title>
                {records.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap">
                    <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={handlePrint}>
                      <i className="ri-printer-line me-1"></i>พิมพ์
                    </Button>
                    <Button variant="outline-success" size="sm" onClick={handleExportExcel}>
                      <i className="ri-file-excel-2-line me-1"></i>Excel
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={handleExportPdf}>
                      <i className="ri-file-pdf-line me-1"></i>PDF
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                {records.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="ri-file-list-3-line fs-3 d-block mb-2"></i>
                    ยังไม่มีบันทึกการสอน
                  </div>
                ) : view === 'card' ? (
                  <div className="p-3">
                    <Row className="g-3">
                      {records.map((r, idx) => (
                        <Col key={r.tr_id} xs={12} md={6} xl={4}>
                          <TrackingRecordCard r={r} idx={idx} formatTime={formatTime} />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                  <Table hover responsive className="mb-0 align-middle">
                    <thead className="table-secondary">
                      <tr>
                        <th className="text-center" style={{ width: 50 }}>#</th>
                        <th className="text-center" style={{ width: 80 }}>วัน</th>
                        <th className="text-center" style={{ width: 120 }}>วันที่</th>
                        <th className="text-center" style={{ width: 80 }}>เวลา</th>
                        <th className="text-center" style={{ width: 70 }}>คาบ</th>
                        <th>หัวข้อที่สอน</th>
                        <th className="text-center" style={{ width: 70 }}>
                          <span className="text-success">มา</span>
                        </th>
                        <th className="text-center" style={{ width: 70 }}>
                          <span className="text-danger">ขาด</span>
                        </th>
                        <th className="text-center" style={{ width: 70 }}>
                          <span className="text-warning">สาย</span>
                        </th>
                        <th className="text-center" style={{ width: 70 }}>
                          <span className="text-info">ลา</span>
                        </th>
                        <th className="text-center" style={{ width: 70 }}>รวม</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r, idx) => {
                        const total = Number(r.total_students) || 0;
                        return (
                          <tr key={r.tr_id}>
                            <td className="text-center text-muted">{idx + 1}</td>
                            <td className="text-center">{r.att_day_th && r.att_day_th}</td>
                            <td className="text-center">
                              {r.att_date_th || "-"}
                            </td>
                            <td className="text-center text-muted">{formatTime(r.created_at)}</td>
                            <td className="text-center">{r.period_order || "-"}</td>
                            <td>{r.tr_topic || <span className="text-muted fst-italic">ไม่ระบุหัวข้อ</span>}</td>
                            <td className="text-center">
                              {total > 0 ? <span className="text-success fw-semibold">{Number(r.presents) || 0}</span> : "-"}
                            </td>
                            <td className="text-center">
                              {total > 0 ? <span className="text-danger fw-semibold">{Number(r.absents) || 0}</span> : "-"}
                            </td>
                            <td className="text-center">
                              {total > 0 ? <span className="text-warning fw-semibold">{Number(r.lates) || 0}</span> : "-"}
                            </td>
                            <td className="text-center">
                              {total > 0 ? <span className="text-info fw-semibold">{Number(r.leaves) || 0}</span> : "-"}
                            </td>
                            <td className="text-center text-muted">{total > 0 ? total : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
